/**
 * VictoryBanner Component
 *
 * Displays the victory banner image with particle convergence animation.
 * Shows winner's username on the scroll section of the banner.
 */

import { useState, useEffect, useMemo } from 'react';
import styles from './VictoryBanner.module.css';

export interface VictoryBannerProps {
  /** The winner's display name */
  winnerName: string;
  /** Callback when animation completes (optional) */
  onAnimationComplete?: () => void;
}

interface Particle {
  id: number;
  startX: number;
  startY: number;
  delay: number;
  color: string;
  size: number;
}

/** Colors matching the banner's magical aesthetic */
const PARTICLE_COLORS = [
  '#9b59b6', // Purple
  '#8e44ad', // Dark purple
  '#3498db', // Blue
  '#2980b9', // Dark blue
  '#f39c12', // Gold
  '#e67e22', // Orange gold
  '#1abc9c', // Cyan/teal (matching sword)
];

/**
 * Generate random position along screen edges
 */
function randomEdgePosition(): { x: number; y: number } {
  const edge = Math.floor(Math.random() * 4);
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  switch (edge) {
    case 0: // Top
      return { x: Math.random() * viewportWidth - viewportWidth / 2, y: -viewportHeight / 2 - 50 };
    case 1: // Right
      return { x: viewportWidth / 2 + 50, y: Math.random() * viewportHeight - viewportHeight / 2 };
    case 2: // Bottom
      return { x: Math.random() * viewportWidth - viewportWidth / 2, y: viewportHeight / 2 + 50 };
    default: // Left
      return { x: -viewportWidth / 2 - 50, y: Math.random() * viewportHeight - viewportHeight / 2 };
  }
}

/**
 * Generate particles for the convergence animation
 */
function generateParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => {
    const pos = randomEdgePosition();
    return {
      id: i,
      startX: pos.x,
      startY: pos.y,
      delay: Math.random() * 300, // Staggered spawn up to 300ms
      color: PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)],
      size: 4 + Math.random() * 8, // 4-12px
    };
  });
}

/**
 * VictoryBanner Component
 */
export function VictoryBanner({ winnerName, onAnimationComplete }: VictoryBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'particles' | 'flash' | 'reveal' | 'complete'>('particles');

  // Generate particles once on mount
  const particles = useMemo(() => generateParticles(45), []);

  useEffect(() => {
    // Phase timing:
    // 0-1000ms: Particles converge
    // 1000-1200ms: Flash
    // 1200-1500ms: Banner reveal
    // 1500-1800ms: Username fade in

    const flashTimer = setTimeout(() => {
      setAnimationPhase('flash');
    }, 900);

    const revealTimer = setTimeout(() => {
      setAnimationPhase('reveal');
      setShowBanner(true);
    }, 1100);

    const usernameTimer = setTimeout(() => {
      setShowUsername(true);
      setAnimationPhase('complete');
      onAnimationComplete?.();
    }, 1500);

    return () => {
      clearTimeout(flashTimer);
      clearTimeout(revealTimer);
      clearTimeout(usernameTimer);
    };
  }, [onAnimationComplete]);

  return (
    <div className={styles.bannerContainer}>
      {/* Particles */}
      <div className={styles.particleContainer}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={styles.particle}
            style={{
              '--start-x': `${particle.startX}px`,
              '--start-y': `${particle.startY}px`,
              '--delay': `${particle.delay}ms`,
              '--color': particle.color,
              '--size': `${particle.size}px`,
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* Flash effect */}
      <div className={`${styles.flash} ${animationPhase === 'flash' ? styles.active : ''}`} />

      {/* Victory Banner Image */}
      <div className={`${styles.bannerWrapper} ${showBanner ? styles.visible : ''}`}>
        <img
          src="/assets/UI/victorybanner.jpeg"
          alt="Victory!"
          className={styles.bannerImage}
        />

        {/* Winner Username on Scroll */}
        <div className={`${styles.usernameContainer} ${showUsername ? styles.visible : ''}`}>
          <span className={styles.username}>{winnerName}</span>
        </div>
      </div>
    </div>
  );
}

export default VictoryBanner;
